<template>
	<Popover class="relative overflow-visible">
		<div class="inline-block" @pointerenter="open = true" @pointerleave="open = false">
			<slot />
		</div>

		<Transition
			enter-active-class="transition duration-200 ease-out"
			enter-from-class="opacity-0"
			enter-to-class="opacity-100"
			leave-active-class="transition duration-150 ease-in"
			leave-from-class="opacity-100"
			leave-to-class="opacity-0"
		>
			<PopoverPanel
				v-if="!disabled && open"
				static
				class="absolute z-50 mt-1 md:mt-2 w-max max-w-sm px-0 inset-full"
				:class="placementClasses"
			>
				<div
					class="overflow-hidden rounded-xl shadow-lg border py-1 md:py-2 px-2 md:px-4"
					:class="typeClasses"
				>
					<slot name="tooltip">
						<p class="font-light text-sm md:text-base" :class="textClasses">
							{{ tooltip }}
						</p>
					</slot>
				</div>
			</PopoverPanel>
		</Transition>
	</Popover>
</template>

<script setup lang="ts">
import { Popover, PopoverPanel } from '@headlessui/vue';
import { computed, ref } from 'vue';

const props = withDefaults(
	defineProps<{
		tooltip?: string;
		placement?: 'left' | 'lefter' | 'leftest' | 'right' | 'center' | 'above';
		type?: 'info' | 'action';
		disabled?: boolean;
	}>(),
	{
		tooltip: '',
		placement: 'lefter',
		type: 'info',
		disabled: false,
	},
);

const open = ref(false);

const placementClasses = computed(() => {
	switch (props.placement) {
		case 'left':
			return '-left-20';
		case 'lefter':
			return '-left-40';
		default:
			return 'left-0';

		case 'leftest':
			return '-left-80';

		case 'right':
			return 'right-0';

		case 'center':
			return 'left-1/2 -translate-x-1/2';

		case 'above':
			return 'left-1/2 -translate-x-1/2 bottom-full -translate-y-24';
	}
});

const typeClasses = computed(() => {
	switch (props.type) {
		case 'info':
		default:
			return 'bg-white dark:bg-martinique-400 border-gray-200 dark:border-martinique-300';

		case 'action':
			return 'bg-portage-500 border-portage-500 dark:bg-gray-200 dark:border-gray-200';
	}
});

const textClasses = computed(() => {
	switch (props.type) {
		case 'info':
		default:
			return 'text-gray-400 dark:text-martinique-100';

		case 'action':
			return 'text-white dark:text-gray-800';
	}
});
</script>
